import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { collection, getDocs } from 'firebase/firestore'
import { db } from '../firebase-config'
import { estimateMinutes, isWorkoutDoc } from '../utils/workout'
import Seo from '../components/Seo'
import { site, breadcrumbSchema } from '../seo'

function Workouts() {
  const [workouts, setWorkouts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState({
    difficulty: 'all',
    category: 'all'
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        const workoutsSnapshot = await getDocs(collection(db, 'workouts'))
        const workoutsList = workoutsSnapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          // Legacy docs from before the exercise/workout split have no `items`.
          // Skip them here; the admin dashboard offers a migration for those.
          .filter(isWorkoutDoc)
        setWorkouts(workoutsList)

        const categoriesSnapshot = await getDocs(collection(db, 'categories'))
        const categoriesList = categoriesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        setCategories(categoriesList.sort((a, b) => a.name.localeCompare(b.name)))

        setLoading(false)
      } catch (error) {
        console.error('Error fetching data:', error)
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  const filteredWorkouts = workouts.filter(workout => {
    return (filter.difficulty === 'all' || workout.difficulty === filter.difficulty) &&
           (filter.category === 'all' || workout.category === filter.category)
  })

  const handleFilterChange = (e) => {
    const { name, value } = e.target
    setFilter(prev => ({
      ...prev,
      [name]: value
    }))
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-16 flex justify-center">
        <div className="text-xl">Loading workouts...</div>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-16 pt-32">
      <Seo
        title={site.routes['/workouts'].title}
        description={site.routes['/workouts'].description}
        schema={breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Workouts', path: '/workouts' }
        ])}
      />
      <h1 className="text-4xl font-bold mb-2 text-center">Workouts</h1>
      <p className="text-gray-600 text-center mb-8">
        Guided sessions that play each exercise back to back. Or{' '}
        <Link to="/exercises" className="text-indigo-600 hover:text-indigo-800 font-medium">
          browse the exercise library
        </Link>.
      </p>

      {workouts.length === 0 ? (
        <div className="text-center text-gray-500">
          <p className="mb-4">No workouts available yet. Check back soon!</p>
          <Link
            to="/exercises"
            className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200"
          >
            Browse individual exercises
          </Link>
        </div>
      ) : (
        <>
          {/* Filters */}
          <div className="bg-gray-100 p-4 rounded-lg mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Difficulty
                </label>
                <select
                  name="difficulty"
                  value={filter.difficulty}
                  onChange={handleFilterChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                >
                  <option value="all">All Difficulties</option>
                  <option value="beginner">Beginner</option>
                  <option value="intermediate">Intermediate</option>
                  <option value="advanced">Advanced</option>
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category
                </label>
                <select
                  name="category"
                  value={filter.category}
                  onChange={handleFilterChange}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2"
                >
                  <option value="all">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.name}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Workouts Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredWorkouts.length === 0 ? (
              <div className="col-span-full text-center text-gray-500 py-8">
                No workouts match your selected filters.
              </div>
            ) : (
              filteredWorkouts.map(workout => (
                <div
                  key={workout.id}
                  className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300 flex flex-col"
                >
                  <div className="p-6 flex-1">
                    <h2 className="text-xl font-bold mb-2">{workout.title}</h2>
                    <p className="text-gray-600 mb-4 line-clamp-2">{workout.description}</p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                        {workout.difficulty}
                      </span>
                      <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                        ~{estimateMinutes(workout)} min
                      </span>
                      <span className="bg-orange-100 text-orange-800 text-xs px-2 py-1 rounded">
                        {workout.items.length} exercises
                      </span>
                      {workout.category && (
                        <span className="bg-purple-100 text-purple-800 text-xs px-2 py-1 rounded">
                          {workout.category}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="px-6 pb-6 flex gap-3">
                    <Link
                      to={`/workouts/${workout.id}/play`}
                      className="flex-1 text-center bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded transition duration-200"
                    >
                      Start
                    </Link>
                    <Link
                      to={`/workouts/${workout.id}`}
                      className="flex-1 text-center border border-indigo-600 text-indigo-600 hover:bg-indigo-50 font-bold py-2 px-4 rounded transition duration-200"
                    >
                      Details
                    </Link>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Call to Action Banner */}
          <div className="bg-indigo-100 rounded-lg p-6 mt-12">
            <div className="max-w-3xl mx-auto text-center">
              <h3 className="text-2xl font-bold text-indigo-800 mb-3">
                Need a personalised workout plan?
              </h3>
              <p className="text-indigo-700 mb-6 max-w-2xl mx-auto">
                Alex can create a customised workout plan tailored to your specific needs, goals, and fitness level.
              </p>
              <Link
                to="/book-consultation"
                className="inline-block bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-lg transition duration-200"
              >
                Book a Consultation
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

export default Workouts
